import { Box, Typography, useTheme } from "@mui/material";
import React, { useState, useEffect } from "react";

// TODO: move this to theme colors
const ROW_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706"
];

function SimpleRow({
  row,
  total,
  dataType,
  useMaxValue,
  maxValue,
  color,
  theme
}) {
  const { label, value, childLabels } = row;

  const percentage = parseFloat(((value / total) * 100).toFixed(1));
  const barPercentage = useMaxValue
    ? parseFloat(((value / maxValue) * 100).toFixed(1))
    : percentage;
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          py: 1.75
        }}
      >
        <Box
          sx={{
            width: 3,
            borderRadius: 2,
            backgroundColor: color,
            flexShrink: 0,
            mr: 2,
            my: 0.25
          }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: "0.9rem",
              mb: 0.75
            }}
          >
            {label}
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.25 }}>
            {childLabels?.map((childLabel, childLabelIndex) => {
              return (
                <Box
                  key={childLabelIndex}
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5
                  }}
                >
                  <Typography
                    sx={{
                      color: childLabel.primary
                        ? color
                        : theme.palette.text.primary,
                      fontWeight: 700,
                      fontSize: childLabel.primary ? "1rem" : "0.875rem",
                      lineHeight: 1.2,
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {childLabel.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                      fontSize: "0.7rem",
                      lineHeight: 1.2
                    }}
                  >
                    {childLabel.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${barPercentage}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${color}55, ${color})`
                }}
              />
            </Box>
            <Typography
              sx={{
                color,
                fontWeight: 700,
                fontSize: "0.8rem",
                minWidth: 42,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0
              }}
            >
              {percentage > 0 ? `${percentage.toFixed(1)}%` : "–"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default function DashboardMiniStatsBarChart({ label, data }) {
  const { total, dataType, useMaxValue } = data || {};
  const [rows, setRows] = useState(null);
  const [maxValue, setMaxValue] = useState(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    if (data?.rows) {
      let _rows = data.rows || [];
      setMaxValue(_rows?.[0]?.value);
      setRows(_rows);
    }
  }, [data]);

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: 1,
        p: { xs: 1.5, sm: 2.5 },
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%"
      }}
    >
      {label?.length ? (
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            color: "primary",
            fontWeight: 600,
            textAlign: "center",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            pt: 2
          }}
        >
          {label}
        </Typography>
      ) : (
        <></>
      )}

      {rows?.length ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column"
          }}
        >
          {rows.map((row, i) => {
            return (
              <SimpleRow
                key={row.id || i}
                row={row}
                total={total}
                useMaxValue={useMaxValue}
                maxValue={maxValue}
                dataType={dataType}
                theme={theme}
                color={ROW_COLORS(isDark)[i % rows.length]}
              />
            );
          })}
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
}
